import { get } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CircularProgressFullScreen } from '../../components/shared_new/CircularProgressFullScreen/CircularProgressFullScreen';
import { config } from '../../config';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import { FeatureFlag } from '../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../store/featureStore/hooks/useFeatureFlag';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import useRouteStore from '../../store/routeStore/useRouteStore';
import { routeRedirectConfigs } from './constants/routeRedirectConfigs';
import { RedirectContext } from './types/RedirectContext';

const UIRevampCleanupGuard = ({ children }: { children: JSX.Element }) => {
  // Controls whether to render the old UI route component
  const [shouldHideChildren, setShouldHideChildren] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const { clearAnalyticListeners } = useAnalyticStore();

  const oldUIGateContext = useRouteStore((state) => state.oldUIGateContext);
  const authRedirectPath = useRouteStore((state) => state.authRedirectPath);
  const nonAuthRedirectPath = useRouteStore(
    (state) => state.computed.nonAuthRedirectPath,
  );

  // Feature flag to enable redirecting from old UI routes to new UI
  const shouldRedirectToNewUI = useFeatureFlag(
    FeatureFlag.ENABLE_OLD_UI_ROUTE_REDIRECT_TO_NEW_UI,
    { userId: undefined }, // explicitly always check flag without userId to avoid default context merging
  );

  // Normalizes paths by removing any trailing slashes (except root path)
  const normalizePathname = (path: string): string =>
    path !== '/' ? path.replace(/\/+$/, '') : path;

  // Navigates to the new UI fallback path based on authentication state
  const navigateToFallback = (token: string | null) => {
    const fallbackPath = token ? authRedirectPath : nonAuthRedirectPath;
    navigate(fallbackPath, { replace: true });
  };

  useEffect(() => {
    // If redirect is disabled, treat as old UI and clean up accordingly
    if (!shouldRedirectToNewUI) {
      // isOnNewUIRoute is being used to display the correct ErrorBoundary component.
      // see App.tsx (errorElement on router)
      useRouteStore.setState({ isOnNewUIRoute: false });

      // the new UI binds event listeners to the document used for analytics on load of the app.
      // we want to remove these listeners when the user navigates to the old UI.
      clearAnalyticListeners();

      setShouldHideChildren(false);

      return;
    }

    // Check for an existing old UI token in localStorage and set it in the parameter store
    const token = localStorage.getItem(config.session.CHECKLIGHT_TOKEN_KEY);
    if (token) {
      useParameterStore.setState({ token });
    }

    // Normalize the pathname and attempt to retrieve the matching redirect config
    const pathname = normalizePathname(location.pathname);
    const routeConfig = get(routeRedirectConfigs, pathname);
    const locationState = location.state || {};

    // If no route config is found, redirect to fallback based on auth state
    if (!routeConfig) {
      navigateToFallback(token);

      return;
    }

    // Destructure config details
    const { requiresGateContext = false, targetPath } = routeConfig;

    // If this route depends on gate context and it doesn't exist yet,
    // allow the old UI child component to render so the context can be initialized.
    // see GateController.tsx routeStore usage
    if (requiresGateContext && !oldUIGateContext) {
      setShouldHideChildren(true);
      return;
    }

    const redirectContext: RedirectContext = {
      pathname,
      locationState,
      gateContext: oldUIGateContext,
    };

    try {
      const target = targetPath(redirectContext);

      if (target) {
        navigate(target, { replace: true });
      } else {
        navigateToFallback(token);
      }
    } catch {
      navigateToFallback(token);
    }
  }, [
    shouldRedirectToNewUI,
    location.pathname,
    location.state,
    oldUIGateContext,
    clearAnalyticListeners,
    authRedirectPath,
    nonAuthRedirectPath,
  ]);

  // Hide children while awaiting the gate context
  if (shouldHideChildren) {
    return (
      <Fragment>
        <CircularProgressFullScreen />
        <div style={{ display: 'none' }}>{children}</div>
      </Fragment>
    );
  }

  return children;
};

export default UIRevampCleanupGuard;
